import { useEffect, useState } from "react";
import TotalInviteSent from "./applicant-invited/TotalInviteSent";
import PendingLogin from "./applicant-invited/PendingLogin";
import ApplicantLoggedIn from "./applicant-invited/ApplicantLoggedIn";
import ModifyInviteModal from "./applicant-invited/ModifyInviteModal";
import TimelineModal from "./TimelineModal";
import { Button } from "antd";
import GuideContentArea from "../../core/common/GuideContentArea";
import {
  dummyData_invited,
  timelineData_invite,
} from "../../core/data/js/dummy-data";
import Footer from "../../core/common/Footer";
import useApi from "../../core/customhook/useApi";
import webservice_url from "../../core/webservice/Webservice_url";
import LoggedIn from "./applicant-invited/LoogedIn";
import WorkInProgress from "./WorkInProgress";
import {
  ClockCircleOutlined,
  FileTextOutlined,
  FundProjectionScreenOutlined,
  LoginOutlined,
  SyncOutlined,
} from "@ant-design/icons";

interface CountData {
  key: string;
  status_id: string;
  label: string;
  count: number;
}

export const statusColors: Record<string, string> = {
  "Pending Login": "bg-warning",
  "Logged In": "bg-soft-success",
  "Login Expired": "bg-soft-danger",
  "Form Approval": "bg-soft-info",
};

export const pendingList = dummyData_invited?.filter((d) =>
  d.status?.toLowerCase()?.includes("pending"),
);
export const loggedinList = dummyData_invited?.filter((d) =>
  d.status?.toLowerCase()?.includes("logged in"),
);
export const expiredList = dummyData_invited?.filter((d) =>
  d.status?.toLowerCase()?.includes("expired"),
);
export const approvalList = dummyData_invited?.filter((d) =>
  d.status?.toLowerCase()?.includes("form"),
);

const guideData = [
  {
    title: "Stage 1 - Invites Sent",
    steps: [
      "All invites sent will by default show a pending login status. The invite will be sent to the candidate, and they have not yet opened the link and logged in to the account.",
    ],
  },
  {
    title: "Stage 2 - Pending Log In",
    steps: [
      "The invite will be sent to the candidate, and they have not yet opened the link and logged in to the account.",
    ],
  },
  {
    title: "Stage 3 - Logged In",
    steps: [
      "The invite has been opened by the candidate, and they have proceeded to fill the form. Form filling is in progress.",
    ],
  },
  {
    title: "Stage 4 - Invites Stopped/Expired",
    steps: [
      "All invites which were not filled by the candidate will expire after a certain period of time.",
      "The default time period is set at 15 working days. If you would like to change this time period, you can do so in the master settings tab (sub tab).",
      "All invites stopped will be added to this bucket and can be resumed at a later date.",
    ],
  },
  {
    title: "Stage 5 - Form Submitted | Awaiting HR Approval",
    steps: [
      "The form has been successfully filled by the applicant and it is now submitted.",
      "The case will either go directly to Aupertz for starting of the background check or it will go to the client's HR for a pre-approval before it gets sent to Aupertz.",
      "By default it will go to Aupertz directly.",
      "If you would like HR approval, this can be triggered in the master settings tab (sub tab).",
    ],
  },
];

const ACTIVE_TAB_KEY = "applicantInvitedActiveTab";

const ApplicantInvited = () => {
  const [data, setData] = useState<CountData[]>([]);
  const getTotalCounts = useApi<any>(webservice_url.getTotalCounts, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const fetchData = async () => {
    try {
      const res = await getTotalCounts.callApi({});
      setData(res?.data ?? []);
    } catch (err) {
      console.error(err);
      setData([]);
    }
  };

  const getCount = (key: string) =>
    data.find((item) => item.key === key)?.count ?? 0;

  const getStatusId = (key: string) =>
    data.find((item) => item.key === key)?.status_id ?? "";

  const totalInvitationsCount = getCount("Total");

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has("tab")) {
      url.searchParams.delete("tab");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  // const ApplicantStats = [
  //   {
  //     id: "total_invite_sent",
  //     label: "Total Invitations",
  //     count: totalInvitationsCount,
  //     component: <TotalInviteSent />,
  //   },
  //   // {
  //   //   id: "pre_bgv",
  //   //   label: "Pre BGV",
  //   //   count: getCount("PREBGV_COMPLETED"),
  //   //   component: <PreBgv />,
  //   // },
  //   {
  //     id: "pending_login",
  //     label: "Login Pending",
  //     count: getCount("PENDING_LOGIN"),
  //     component: <PendingLogin statusId={getStatusId("PENDING_LOGIN")} />,
  //   },
  //   {
  //     id: "logged_in",
  //     label: "Logged In",
  //     count: getCount("LOGIN"),
  //     component: <LoggedIn statusId={getStatusId("LOGIN")} />,
  //   },
  //   {
  //     id: "applicant_logged_in",
  //     label: "In Progress",
  //     count: getCount("PENDING_SUBMISSION"),
  //     component: (
  //       <ApplicantLoggedIn statusId={getStatusId("PENDING_SUBMISSION")} />
  //     ),
  //   },
  //   {
  //     id: "",
  //     label: "Live Case Progress",
  //     count: totalInvitationsCount,
  //     component: <WorkInProgress statusId={getStatusId("")} />,
  //   },
  // ];
  const ApplicantStats = [
    {
      id: "total_invite_sent",
      label: "Total Invitations",
      icon: <FileTextOutlined />,
      count: totalInvitationsCount,
      component: <TotalInviteSent />,
    },
    {
      id: "pending_login",
      label: "Pending Login",
      icon: <ClockCircleOutlined />,
      count: getCount("PENDING_LOGIN"),
      component: <PendingLogin statusId={getStatusId("PENDING_LOGIN")} />,
    },
    {
      id: "logged_in",
      label: "Logged In",
      icon: <LoginOutlined />,
      count: getCount("LOGIN"),
      component: <LoggedIn statusId={getStatusId("LOGIN")} />,
    },
    {
      id: "applicant_logged_in",
      label: "In Progress",
      icon: <SyncOutlined />,
      count: getCount("PENDING_SUBMISSION"),
      component: (
        <ApplicantLoggedIn statusId={getStatusId("PENDING_SUBMISSION")} />
      ),
    },
    {
      id: "work_in_progress",
      label: "Live Case Progress",
      icon: <FundProjectionScreenOutlined />,
      count: totalInvitationsCount,
      component: <WorkInProgress statusId={getStatusId("")} />,
    },
  ];
  const [showTimeline, setShowTimeline] = useState<boolean>(false);
  const [showModifyInvite, setShowModifyInvite] = useState<boolean>(false);
  const [showGuide, setShowGuide] = useState<boolean>(false);

  const [activeTab, setActiveTab] = useState<string>(() => {
    try {
      return localStorage.getItem(ACTIVE_TAB_KEY) || "total_invite_sent";
    } catch {
      return "total_invite_sent";
    }
  });

  useEffect(() => {
    const valid = ApplicantStats.some((s) => s.id === activeTab);
    if (!valid && ApplicantStats[0]?.id) {
      setActiveTab(ApplicantStats[0].id);
    }
  }, [data]);

  const activeItem = ApplicantStats.find((i) => i.id == activeTab);
  const activeComponent = activeItem ? activeItem.component : <></>;

  const handleTabClick = (d: { id: string }) => {
    setActiveTab(d.id);
    try {
      localStorage.setItem(ACTIVE_TAB_KEY, d.id);
    } catch (err) {
      console.error("Failed to save active tab to localStorage:", err);
    }
  };

  const CaseMark = () => (
    <svg
      className="tcp-case-mark"
      viewBox="0 0 40 32"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="0" y="14" width="4" height="18" rx="1.5" />
      <rect x="9" y="8" width="4" height="24" rx="1.5" />
      <rect x="18" y="0" width="4" height="32" rx="1.5" />
      <rect x="27" y="10" width="4" height="22" rx="1.5" />
      <rect x="36" y="18" width="4" height="14" rx="1.5" />
    </svg>
  );
  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="tcp-header-title-row mb-3">
          <CaseMark />
          <div>
            <h4 className="tcp-title">Candidate Tracking</h4>
            <p className="tcp-subtitle">
              Track candidate cases, monitor screening progress, and manage invitations in one place.
            </p>
          </div>
        </div>
        <>
          {/* <ul className="invite-tabs">
            {ApplicantStats.map((tab) => (
              <li key={tab.id}>
                <button
                  type="button"
                  className={`invite-btn ${
                    activeTab === tab.id ? "active" : ""
                  }`}
                  onClick={() => handleTabClick(tab)}
                >
                  <span className="d-block">{tab.label}</span>
                  {typeof tab.count === "number" && (
                    <span className="d-block fw-semibold">{tab.count}</span>
                  )}
                </button>
              </li>
            ))}
          </ul> */}
          <ul className="invite-tabs">
            {ApplicantStats.map((tab, index) => (
              <li key={tab.id || `applicant-tab-${index}`}>
                <button
                  type="button"
                  className={`invite-btn ${
                    activeTab === tab.id ? "active" : ""
                  }`}
                  onClick={() => handleTabClick(tab)}
                >
                  <span className="d-flex align-items-center justify-content-center gap-2">
                    {tab.icon}
                    <span>{tab.label}</span>
                  </span>

                  {typeof tab.count === "number" && (
                    <span className="d-block fw-semibold">{tab.count}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
          <>
            <div className="text-end">
              <Button
                className="border-secodary"
                onClick={() => setShowGuide((prev) => !prev)}
              >
                <i className="ti ti-info-circle-filled fs-16"></i>
                <div className="pb-1 ">User Guide</div>
              </Button>
            </div>

            <GuideContentArea
              maxHeight={"400px"}
              showGuide={showGuide}
              handleClick={() => setShowGuide(false)}
              title={"Applicant Invited"}
              content={
                <div>
                  <div className="pb-2">
                    The applicant will receive the user credentials to access
                    the Aupertz applicant portal through an invite.
                  </div>
                  <div className="pb-4">
                    The applicant will log in to fill the applicant form (ADF)
                    and submit all the required data for screening.
                  </div>
                  {guideData?.map((d) => (
                    <div className="mb-3" style={{ maxWidth: "900px" }}>
                      <h6 className="fw-semibold pb-1">{d.title}</h6>
                      {d?.steps?.map((i, idx) => (
                        <div className="d-flex gap-2 py-1">
                          <div>{idx + 1}.</div>
                          <div>{i}</div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              }
            />
          </>

          <div className="">{activeComponent}</div>

          {showTimeline && (
            <TimelineModal
              timelineData={timelineData_invite}
              onClose={() => setShowTimeline(false)}
            />
          )}
          {showModifyInvite && (
            <ModifyInviteModal
              handleClose={() => setShowModifyInvite(false)}
              handleSubmit={(d) => {
                setShowModifyInvite(false);
              }}
            />
          )}
        </>
      </div>

      <Footer />
    </div>
  );
};
export default ApplicantInvited;
